"use client";

import { QueryClientProvider } from "@tanstack/react-query";
import { usePathname } from "next/navigation";
import { ThemeProvider, useTheme } from "next-themes";
import { type CSSProperties, Suspense, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Toaster } from "sonner";
import { InteractionScale } from "@/components/interaction-scale";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  initializeCloudWatchRum,
  setCloudWatchRumPage,
} from "@/lib/client/cloudwatch-rum";
import { getClientQueryClient } from "@/lib/client/query-client";

function CloudWatchRumProvider() {
  const pathname = usePathname();

  useEffect(() => {
    setCloudWatchRumPage(pathname);
  }, [pathname]);

  useEffect(() => {
    void initializeCloudWatchRum();
  }, []);

  return null;
}

function AppToaster() {
  const { resolvedTheme } = useTheme();
  const [body, setBody] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setBody(document.body);
  }, []);

  if (!body) return null;

  // app-root has `isolation: isolate` which creates a new stacking context,
  // trapping Sonner's z-index inside it. Render directly to document.body
  // so the toaster participates in the root stacking context and stays above
  // dialogs and other overlays.
  return createPortal(
    <Toaster
      position="top-center"
      richColors
      closeButton
      theme={resolvedTheme === "dark" ? "dark" : "light"}
      style={
        {
          "--border-radius": "var(--radius-md)",
        } as CSSProperties
      }
      toastOptions={{
        classNames: { closeButton: "border-0! shadow-composer" },
        closeButtonAriaLabel: "閉じる",
        style: {
          border: "none",
          boxShadow: "var(--shadow-composer)",
          minWidth: "min(360px, calc(100vw - 32px))",
          maxWidth: "calc(100vw - 32px)",
        },
      }}
    />,
    body,
  );
}

export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(() => getClientQueryClient());
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Suspense fallback={null}>
            <CloudWatchRumProvider />
          </Suspense>
          {children}
          <InteractionScale />
          <AppToaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
